import useTranslation from 'next-translate/useTranslation';
import {
  Container,
  DeleteButton,
  FileUploadContainer,
  FileUploadLoadingContainer,
  FileUploadWrapper,
  IconUpload,
  ImagePreview,
  Input,
  PendingIcon,
  Spinner,
  TextContainer,
} from './styles';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { validImageTypes } from '@/constants/validImageTypes';
import { FileDragAndDropProps } from './props';
import { useNotificationService } from '@/hooks/notifications/useNotificationService';

export default function FileDragAndDrop({
  label,
  removeFile: removeFileFromForm,
  addFile,
  isLoading,
  progress,
  isError,
}: FileDragAndDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { NotificationsService } = useNotificationService();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | undefined>(
    undefined,
  );
  const [dragging, setDragging] = useState(false);
  const dropAreaRef = useRef<HTMLLabelElement>(null);
  const { t } = useTranslation('common');

  const uploadFile = useCallback(
    async (files: FileList | undefined | null) => {
      if (!files || files.length === 0) {
        NotificationsService.error({
          title: 'form_error_title',
          message: 'form_error_description',
        });
        return;
      }

      const file: File = files[0];

      if (!validImageTypes.includes(file.type)) {
        NotificationsService.error({
          title: 'form_error_title',
          message: 'form_error_file_type',
          params: {
            file_type: files[0].name.split('.').pop(),
          },
        });
        return;
      }

      try {
        addFile(file);
        setFile(file);
        handleImagePreview(file);
      } catch (_e: unknown) {
        NotificationsService.error({
          title: 'form_error_title',
          message: 'file_upload_error_description',
        });
      }
    },
    [addFile, NotificationsService],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);

      const files = event.dataTransfer?.files;

      uploadFile(files);
    },
    [uploadFile],
  );

  function handleDragAndDrop() {
    const dragAreaElement = dropAreaRef.current;
    dragAreaElement?.addEventListener('dragover', handleDragOver);
    dragAreaElement?.addEventListener('drop', handleDrop);
    dragAreaElement?.addEventListener('dragleave', handleDragLeave);
    dragAreaElement?.addEventListener('dragenter', handleDragEnter);

    return () => {
      dragAreaElement?.removeEventListener('dragover', handleDragOver);
      dragAreaElement?.removeEventListener('drop', handleDrop);
      dragAreaElement?.addEventListener('dragleave', handleDragLeave);
      dragAreaElement?.addEventListener('dragenter', handleDragEnter);
    };
  }

  useEffect(handleDragAndDrop, [handleDrop]);

  function handleImagePreview(file: File) {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => {
      const fileContent = reader.result;
      setImagePreview(`${fileContent}`);
    };
  }

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.target === dropAreaRef.current) {
      setDragging(true);
    }
  }

  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.target === dropAreaRef.current) {
      setDragging(false);
    }
  }

  function removeFile(e: MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    setFile(undefined);
    removeFileFromForm();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  return (
    <Container isDragging={dragging} ref={dropAreaRef}>
      <IconUpload className="fad fa-cloud-upload" />
      <TextContainer>
        <h5>{label}</h5>
        <p>{t('file_upload_description')}</p>
      </TextContainer>

      {file && (
        <FileUploadContainer isLoading={isLoading} progress={progress}>
          <FileUploadWrapper>
            <FileUploadLoadingContainer
              isComplete={
                progress !== null && progress === 100 && !isLoading && !isError
              }
            >
              {isLoading && <Spinner className="fas fa-circle-notch fa-spin" />}

              {!isLoading &&
                !isError &&
                file &&
                imagePreview &&
                progress === 0 && (
                  <ImagePreview
                    alt="dnd-preview"
                    format="rectangle"
                    src={imagePreview}
                    secondarySkeleton
                  />
                )}

              {!isLoading && isError && (
                <PendingIcon className="fas fa-exclamation" />
              )}
            </FileUploadLoadingContainer>
            <span>{file.name}</span>
          </FileUploadWrapper>

          <DeleteButton onClick={removeFile} disabled={isLoading}>
            <i className="fad fa-trash-alt" />
          </DeleteButton>
        </FileUploadContainer>
      )}

      <Input
        ref={inputRef}
        type="file"
        onChange={e => {
          uploadFile(e.target.files);
        }}
      />
    </Container>
  );
}
